import React from "react";
import moment from "moment";
import { getQueryParams, fetchProfileData, fetchFinancialData } from './utils';
import GuideDashboardHeader from "./GuideDashboardHeader";
import Footer from "./Footer";

const DEFAULT_CUSTOMER_SUMMARY = {
    totalCustomers: 0,
    activeSubscribers: 0,
    totalCustomerRevenue: 0
};

const STATUS_STYLES = {
    "active-subscriber": { background: "#e4f7ea", color: "#1f7a3d" },
    "past-customer": { background: "#f3f4f6", color: "#4b5563" },
    unpaid: { background: "#fff3cd", color: "#8a6d3b" },
    mixed: { background: "#e7f0ff", color: "#1d4ed8" }
};

const ManageFinances = (props) => {
    // Deleting state for products
    const [deletingProduct, setDeletingProduct] = React.useState(null); // { type, frequency, subcategory }
    const [deletingProductLoading, setDeletingProductLoading] = React.useState(false);
    const [deletingProductError, setDeletingProductError] = React.useState("");

        
    // Editing state for products
    const [editingProduct, setEditingProduct] = React.useState(null); // { type, frequency, subcategory }
    const [editingProductPrice, setEditingProductPrice] = React.useState("");
    const [editingProductLoading, setEditingProductLoading] = React.useState(false);
    const [editingProductError, setEditingProductError] = React.useState("");
    

                // Product adding state
    const [productCategory, setProductCategory] = React.useState("");
    const [productFrequency, setProductFrequency] = React.useState("");
    const [productSubcategory, setProductSubcategory] = React.useState("");
    const [productPrice, setProductPrice] = React.useState("");
    const [productLoading, setProductLoading] = React.useState(false);
    const [productSuccess, setProductSuccess] = React.useState("");
    const [productError, setProductError] = React.useState("");

    const TEXT_SUBSCRIPTION_CATEGORY = "text-subscription";
    const TEXT_AND_CALL_SUPPORT_CATEGORY = "text-and-call-support";
    const supportOnlyProductCategories = [TEXT_SUBSCRIPTION_CATEGORY, TEXT_AND_CALL_SUPPORT_CATEGORY];
    const recurringProductCategories = ["subscription", ...supportOnlyProductCategories];
    const productCategories = ["onetime", "subscription", ...supportOnlyProductCategories];
    const productCategoryLabels = {
        onetime: "One-Time Follow-up Call",
        subscription: "Call Subscription",
        [TEXT_SUBSCRIPTION_CATEGORY]: "Text Subscription",
        [TEXT_AND_CALL_SUPPORT_CATEGORY]: "Text and Call Support"
    };
    const subscriptionFrequencies = [
        "daily",
        "weekly",
        "bi-weekly",
        "monthly"
    ];
    const textSubscriptionFrequencies = [
        "weekly",
        "monthly"
    ];
    const textAndCallSupportFrequencies = [
        "weekly",
        "monthly"
    ];

    const productSubcategories = [
        "fifteenminutes",
        "thirtyminutes",
        "fortyfiveminutes",
        "sixtyminutes"
    ];

     const productSubcategoryNames = [
        "Fifteen Minutes",
        "Thirty Minutes",
        "Forty-Five Minutes",
        "Sixty Minutes"
    ];

    const isSupportOnlyProductCategory = (category) => supportOnlyProductCategories.includes(category);
    const isRecurringProductCategory = (category) => recurringProductCategories.includes(category);
    const getSupportOnlyProductSubcategory = (category) => {
        if (category === TEXT_AND_CALL_SUPPORT_CATEGORY) {
            return "text-and-call";
        }
        return "text";
    };
    const getProductDescription = (category, subcategory, frequency) => {
        if (category === TEXT_SUBSCRIPTION_CATEGORY) {
            return `Text Subscription (${frequency})`;
        }
        if (category === TEXT_AND_CALL_SUPPORT_CATEGORY) {
            return `Text and Call Support (${frequency})`;
        }
        return `${category} - ${subcategory}` + (category === "subscription" && frequency ? ` (${frequency})` : "");
    };

    // Intro Call price state
    const [introCallPrice, setIntroCallPrice] = React.useState();
    const [editingIntroCall, setEditingIntroCall] = React.useState(false);
    const [introCallCurrency, setIntroCallCurrency] = React.useState("usd");
    const [introCallLoading, setIntroCallLoading] = React.useState(false);
    const [introCallSuccess, setIntroCallSuccess] = React.useState("");
    const [introCallError, setIntroCallError] = React.useState("");   
   

    const [stripeConnected, setStripeConnected] = React.useState(false);
    const [loadingConnect, setLoadingConnect] = React.useState(false);
    const [checkingConnect, setCheckingConnect] = React.useState(true);
    const [stripeAccountUrl, setStripeAccountUrl] = React.useState(null);
    const [guideProfileData, setGuideProfileData] = React.useState(null);
    const [paymentProcessor, setPaymentProcessor] = React.useState(null); // For future use if we support multiple processors
    const [useAlternatePaymentProcessor, setUseAlternatePaymentProcessor] = React.useState(false); // For future use if needed
    const [processorNotConnected, setProcessorNotConnected] = React.useState(true);
    const [processorNotConfirmed, setProcessorNotConfirmed] = React.useState(true);
    const [payoutInfoCollected, setPayoutInfoCollected] = React.useState(false);
    const [wisePaymentsInfo, setWisePaymentsInfo] = React.useState(null);
    // Wise onboarding state variables
    const [wiseEmail, setWiseEmail] = React.useState("");
    const [wiseFullName, setWiseFullName] = React.useState("");
    const [wiseCityOfResidence, setWiseCityOfResidence] = React.useState("");
    const [wiseUpiId, setWiseUpiId] = React.useState("");
    const [wiseStreetAddress, setWiseStreetAddress] = React.useState("");
    const [wiseCity, setWiseCity] = React.useState("");
    const [wisePostalCode, setWisePostalCode] = React.useState("");
    const [totalEarnings, setTotalEarnings] = React.useState(0);
    const [cashedOutEarnings, setCashedOutEarnings] = React.useState(0);
    const [availableEarnings, setAvailableEarnings] = React.useState(0);
    const [lastPayoutRequestData, setLastPayoutRequestData] = React.useState(null);
    const [hasFundsToCashOut, setHasFundsToCashOut] = React.useState(false);
    const [showSection, setShowSection] = React.useState("your-products"); // "your-products", "customers", or "financial-dashboard"
    const [customerSummary, setCustomerSummary] = React.useState(DEFAULT_CUSTOMER_SUMMARY);
    const [customerCards, setCustomerCards] = React.useState([]);
    const [customersLoading, setCustomersLoading] = React.useState(false);
    const [customersLoaded, setCustomersLoaded] = React.useState(false);
    const [customersError, setCustomersError] = React.useState("");
    const [selectedOfferKeys, setSelectedOfferKeys] = React.useState({});
    const [sendingPromptKey, setSendingPromptKey] = React.useState(null);
    const [customerToast, setCustomerToast] = React.useState(null);
    const [commerceHistoryModal, setCommerceHistoryModal] = React.useState(null);
    

    // Check Stripe connection status
    // Helper to set a cookie
    function setCookie(name, value, days) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days*24*60*60*1000));
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie = name + "=" + (value || "")  + expires + "; path=/; SameSite=Lax";
    }

    // Helper to get a cookie
    function getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for(let i=0;i < ca.length;i++) {
            let c = ca[i];
            while (c.charAt(0)==' ') c = c.substring(1,c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
        }
        return null;
    }

    const showToast = (type, message) => {
        setCustomerToast({ type, message });
    };

    React.useEffect(() => {
        if (!customerToast) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setCustomerToast(null);
        }, 3500);

        return () => window.clearTimeout(timeoutId);
    }, [customerToast]);

    const formatMoneyFromCents = (amount, currency = "usd") => {
        const normalizedAmount = Number(amount || 0) / 100;

        try {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: (currency || "usd").toUpperCase()
            }).format(normalizedAmount);
        } catch (error) {
            return `$${normalizedAmount.toFixed(2)}`;
        }
    };

    const formatDateTime = (value) => {
        if (!value) {
            return "No activity yet";
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return "No activity yet";
        }

        return date.toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const getCustomerInitials = (name) => {
        if (!name) {
            return "?";
        }

        return name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join("") || "?";
    };

    const getStatusStyle = (status) => {
        return STATUS_STYLES[status] || { background: "#f3f4f6", color: "#374151" };
    };

    const makeOfferKey = (customerId, offer) => {
        return [customerId, offer.productCategory, offer.frequency || "once", offer.subcategory].join("::");
    };

    const getSelectedOfferKey = (customer) => {
        const offers = customer.availableOffers || [];
        if (!offers.length) {
            return "";
        }

        const savedOfferKey = selectedOfferKeys[customer.customerId];
        if (savedOfferKey && offers.some((offer) => makeOfferKey(customer.customerId, offer) === savedOfferKey)) {
            return savedOfferKey;
        }

        const firstSendableOffer = offers.find((offer) => offer.canPrompt);
        return makeOfferKey(customer.customerId, firstSendableOffer || offers[0]);
    };

    const getSelectedOffer = (customer) => {
        const selectedOfferKey = getSelectedOfferKey(customer);
        return (customer.availableOffers || []).find((offer) => makeOfferKey(customer.customerId, offer) === selectedOfferKey) || null;
    };

    const handleSelectedOfferChange = (customerId, offerKey) => {
        setSelectedOfferKeys((currentSelections) => ({
            ...currentSelections,
            [customerId]: offerKey
        }));
    };

    const openCommerceHistoryModal = (customerId, type) => {
        setCommerceHistoryModal({ customerId, type });
    };

    const closeCommerceHistoryModal = () => {
        setCommerceHistoryModal(null);
    };

    const fetchCustomerCommerce = async (forceRefresh = false) => {
        if (customersLoading) {
            return;
        }
        if (customersLoaded && !forceRefresh) {
            return;
        }

        setCustomersLoading(true);
        setCustomersError("");

        try {
            const token = window.localStorage.getItem('guideToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/guide/finances/customers`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (data.success) {
                setCustomerSummary(data.summary || DEFAULT_CUSTOMER_SUMMARY);
                setCustomerCards(Array.isArray(data.customers) ? data.customers : []);
                setCustomersLoaded(true);
            } else {
                setCustomersError(data.error || "Failed to load customers.");
            }
        } catch (error) {
            setCustomersError("Unable to load customers right now.");
        }

        setCustomersLoading(false);
    };

    React.useEffect(() => {
        if (showSection === "customers") {
            fetchCustomerCommerce();
        }
    }, [showSection]);

    const handlePromptSend = async (customerId, offer) => {
        if (!offer) {
            showToast("error", "Please select an offer before sending an invoice.");
            return;
        }

        const requestKey = makeOfferKey(customerId, offer);
        setSendingPromptKey(requestKey);

        try {
            const token = window.localStorage.getItem('guideToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/guide/customers/${customerId}/send-payment-prompt`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    productCategory: offer.productCategory,
                    frequency: offer.frequency,
                    subcategory: offer.subcategory,
                    customIntroLine: ""
                })
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok && data.success) {
                showToast("success", "Invoice sent successfully.");
                await fetchCustomerCommerce(true);
                return;
            }

            if (response.status === 429) {
                showToast("error", data.error || "A recent prompt was already sent. Please wait before trying again.");
                return;
            }

            if (response.status === 409) {
                showToast("error", data.error || "This offer cannot be sent right now for this customer.");
                await fetchCustomerCommerce(true);
                return;
            }

            if (response.status === 403) {
                showToast("error", data.error || "This customer cannot be messaged from your guide account.");
                return;
            }

            if (response.status === 404) {
                showToast("error", data.error || "The customer or offer could not be found.");
                await fetchCustomerCommerce(true);
                return;
            }

            if (response.status === 502) {
                showToast("error", data.error || "The prompt was logged but could not be sent. Please check again before retrying.");
                await fetchCustomerCommerce(true);
                return;
            }

            showToast("error", data.error || "Failed to send invoice.");
        } catch (error) {
            showToast("error", "Network error while sending invoice.");
        } finally {
            setSendingPromptKey(null);
        }
    };

     const handleSetIntroCallPrice = async () => {
        setIntroCallLoading(true);
        setIntroCallSuccess("");
        setIntroCallError("");
        try {
            const token = window.localStorage.getItem('guideToken');
            const response = await fetch(process.env.REACT_APP_API_URL + "/stripe/set-intro-call-price", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    guideID: guideProfileData?._id,
                    amount: Math.round(Number(introCallPrice) * 100), // Stripe expects cents
                    currency: introCallCurrency,
                    description: "Intro Call"
                })
            });
            const data = await response.json();
            if (data.success) {
                setIntroCallSuccess("Intro Call price updated!");
            } else {
                setIntroCallError(data.error || "Failed to set price.");
            }
        } catch (err) {
            setIntroCallError("Network error.");
        }
        setIntroCallLoading(false);
    };

    const checkStripeConnection = async () => {
        const response = await fetch(process.env.REACT_APP_API_URL + "/stripe/guide/connected", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${window.localStorage.getItem('guideToken')}`
            }
        });
        const data = await response.json();
        setStripeConnected(data.connected);
        setCheckingConnect(false);
        if(data.connected && data.url) {
            setStripeAccountUrl(data.url);
        }
    };

    const onBoardToStripe = async () => {
        setLoadingConnect(true);
        // Store guideToken in a cookie before redirect
        const token = window.localStorage.getItem('guideToken');
        if (token) {
            setCookie('guideToken', token, 7); // 7 days expiry
        }
        const response = await fetch(process.env.REACT_APP_API_URL + "/stripe/guide/onboard-to-stripe", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const data = await response.json();
        if (data.url) {
            window.open(data.url, "_blank"); // Redirect to Stripe onboarding
        } else {
            console.error("Failed to get Stripe onboarding URL");
        }
    }

     // Handler for Wise onboarding submission
    const handleWiseSubmit = async () => {
        const token = window.localStorage.getItem('guideToken');
        const payload = {
            email: wiseEmail,
            fullName: wiseFullName,
            cityOfResidence: wiseCityOfResidence,
            upiId: wiseUpiId,
            address: {
                street: wiseStreetAddress,
                city: wiseCity,
                postalCode: wisePostalCode
            }
        };
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/guide/payment-onboard/wise", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                console.log("Wise onboarding submitted successfully! We'll send you a small payment and confirm it worked. Expect outreach from a Boggler team member soon!");
                setProcessorNotConnected(false);
            } else {
                console.log("Failed to submit Wise onboarding: " + (data.message || "Unknown error"));
            }
        } catch (error) {
            console.log("Error submitting Wise onboarding: " + error.message);
        }
    };

    const fetchFinances = async () => {
            // Future function to fetch any finance-related data if needed
            let financialData = null;
            try {
                financialData = await fetchFinancialData();
                financialData = JSON.parse(JSON.stringify(financialData)); // Deep copy to avoid React state issues
                if(financialData.success) {
                    setProcessorNotConfirmed(!financialData.payouts_enabled);
                    setProcessorNotConnected(!financialData.payout_info_collected);
                    setPayoutInfoCollected(financialData.payout_info_collected);
                    setWisePaymentsInfo(financialData.wise_payments_info);
                    setTotalEarnings(financialData.totalEarnings);
                    setCashedOutEarnings(financialData.cashedOut);
                    setAvailableEarnings(financialData.notCashedOut);
                    setLastPayoutRequestData(financialData.lastPayoutRequest);
                    if(financialData.payout_process === "wise") {
                        setPaymentProcessor({ name: "wise", logo: "https://buckish-ghosted-maps.s3.us-west-1.amazonaws.com/wise-logo.png" });
                        setWiseEmail(financialData.wise_payments_info?.email || "");
                        setWiseFullName(financialData.wise_payments_info?.fullName || "");
                        setWiseCityOfResidence(financialData.wise_payments_info?.cityOfResidence || "");
                        setWiseUpiId(financialData.wise_payments_info?.upiId || "");
                        setWiseCity(financialData.wise_payments_info?.address?.city || "");
                        setWisePostalCode(financialData.wise_payments_info?.address?.postalCode || "");
                        setWiseStreetAddress(financialData.wise_payments_info?.address?.street || "");
                        if(financialData.notCashedOut > 0) {
                            setHasFundsToCashOut(true);
                        } else {
                            setHasFundsToCashOut(false);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching financial data:", error);
                return;
            }
        }

    const fetchData = async () => {
            let data = null;
            try {
                data = await fetchProfileData();
                data = JSON.parse(JSON.stringify(data)); // Deep copy to avoid React state issues
            } catch (error) {
                console.error("Error fetching profile data:", error);
                return;
            }
            if (!data.success) {
                if (data.expiredToken) {
                    localStorage.removeItem('guideToken');
                    localStorage.removeItem('guideTokenExpiry');
                }
            } else {
                setGuideProfileData(data?.guide);
                setIntroCallPrice(data?.guide?.stripe_intro_call_amount ? (data.guide.stripe_intro_call_amount / 100).toString() : ""); // Convert cents to dollars for display
                if(data?.guide?.payout_processor === "wise") {
                    if(data?.guide?.payout_info_collected) {
                        setProcessorNotConnected(false);
                    }
                    if(data?.guide?.payouts_enabled) {
                        setProcessorNotConfirmed(false);
                    }
                }
                if(data?.guide?.country?.name === "India") {
                    setUseAlternatePaymentProcessor(true);
                    setCheckingConnect(false);
                    setPaymentProcessor({ name: "wise", logo: "https://buckish-ghosted-maps.s3.us-west-1.amazonaws.com/wise-logo.png" });
                } else {
                    checkStripeConnection();
                }
            }
        }

    React.useEffect(() => {
        const queryParams = getQueryParams(window.location.search);        

        fetchData();
        fetchFinances();

        // On return from Stripe, if guideToken is missing, restore from cookie
        if (!window.localStorage.getItem('guideToken')) {
            const cookieToken = getCookie('guideToken');
            if (cookieToken) {
                window.localStorage.setItem('guideToken', cookieToken);
            }
        }

        switch(queryParams.flow) {
            case 'stripe_connected_complete':
                // Handle successful onboarding
                console.log('Stripe onboarding completed for account:', queryParams.account);
                // Show success message, refresh account status
                window.location.href = "/manage-finances?stripe_refresh=true"; // Refresh the page to update status
                break;
            case 'stripe_refresh':
                // Handle incomplete/expired onboarding
                console.log('Stripe onboarding needs to be refreshed');
                // Show message to complete setup
                window.location.href = "/manage-finances"; // Refresh the page to update status
                break;
            default:
                // Normal page load
                break;
        }


    }, []);

    const handleRequestPayout = async () => {
        const token = window.localStorage.getItem('guideToken');
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + "/guide/request-payout", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ 
                    amount: availableEarnings,
                    dateUnix: moment().valueOf() 
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchFinances()
                console.log("Payout request submitted successfully! We will process your payout within 1-2 business days. Thank you for your patience!");
            } else {
                console.log("Failed to submit payout request: " + (data.message || "Unknown error"));
            }
        } catch (error) {
            console.log("Error submitting payout request: " + error.message);
        }
    };


    // Consolidated add/edit product function
    const handleUpsertProduct = async () => {
        if (editingProduct) {
            // Editing existing product
            setEditingProductLoading(true);
            setEditingProductError("");
            try {
                const token = window.localStorage.getItem('guideToken');
                const guideID = guideProfileData?._id;
                if (!guideID) {
                    setEditingProductError("Guide ID not found. Please reload the page.");
                    setEditingProductLoading(false);
                    return;
                }
                const payload = {
                    guideID,
                    category: editingProduct.type,
                    subcategory: editingProduct.subcategory,
                    frequency: isRecurringProductCategory(editingProduct.type) ? editingProduct.frequency : undefined,
                    amount: Math.round(Number(editingProductPrice) * 100),
                    currency: "usd",
                    description: getProductDescription(editingProduct.type, editingProduct.subcategory, editingProduct.frequency)
                };
                const response = await fetch(process.env.REACT_APP_API_URL + "/stripe/create-or-update-product-price", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });
                const data = await response.json();
                if (data.success) {
                    setEditingProduct(null);
                    setEditingProductPrice("");
                    setEditingProductLoading(false);
                    setProductSuccess("Product price updated!");
                    fetchData();
                } else {
                    setEditingProductError(data.error || "Failed to update product.");
                    setEditingProductLoading(false);
                }
            } catch (err) {
                setEditingProductError("Network error.");
                setEditingProductLoading(false);
            }
        } else {
            // Adding new product
            setProductLoading(true);
            setProductSuccess("");
            setProductError("");
            try {
                const token = window.localStorage.getItem('guideToken');
                const guideID = guideProfileData?._id;
                if (!guideID) {
                    setProductError("Guide ID not found. Please reload the page.");
                    setProductLoading(false);
                    return;
                }
                const payload = {
                    guideID,
                    category: productCategory,
                    subcategory: isSupportOnlyProductCategory(productCategory) ? getSupportOnlyProductSubcategory(productCategory) : productSubcategory,
                    frequency: isRecurringProductCategory(productCategory) ? productFrequency : undefined,
                    amount: Math.round(Number(productPrice) * 100),
                    currency: "usd",
                    description: getProductDescription(productCategory, productSubcategory, productFrequency)
                };
                const response = await fetch(process.env.REACT_APP_API_URL + "/stripe/create-or-update-product-price", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });
                const data = await response.json();
                if (data.success) {
                    setProductSuccess("Product added/updated successfully!");
                    setProductCategory("");
                    setProductSubcategory("");
                    setProductFrequency("");
                    setProductPrice("");
                    fetchData();
                } else {
                    setProductError(data.error || "Failed to add/update product.");
                }
            } catch (err) {
                setProductError("Network error.");
            }
            setProductLoading(false);
        }
    };

    const handleDeleteProduct = async (product) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        setDeletingProduct(product);
        setDeletingProductLoading(true);
        setDeletingProductError("");
        try {
            const token = window.localStorage.getItem('guideToken');
            const guideID = guideProfileData?._id;
            if (!guideID) {
                setDeletingProductError("Guide ID not found. Please reload the page.");
                setDeletingProductLoading(false);
                return;
            }
            const payload = {
                guideID,
                category: product.type,
                subcategory: product.subcategory,
                frequency: isRecurringProductCategory(product.type) ? product.frequency : undefined,
            };
            const response = await fetch(process.env.REACT_APP_API_URL + "/stripe/delete-product-price", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                setDeletingProduct(null);
                setDeletingProductLoading(false);
                setProductSuccess("Product deleted!");
                fetchData();
            } else {
                setDeletingProductError(data.error || "Failed to delete product.");
                setDeletingProductLoading(false);
            }
        } catch (err) {
            setDeletingProductError("Network error.");
            setDeletingProductLoading(false);
        }
    };

    const commerceHistoryCustomer = commerceHistoryModal
        ? customerCards.find((customer) => customer.customerId === commerceHistoryModal.customerId)
        : null;
    const commerceHistoryItems = commerceHistoryCustomer
        ? (commerceHistoryModal.type === "payments" ? commerceHistoryCustomer.payments : commerceHistoryCustomer.subscriptions) || []
        : [];
    const commerceHistoryTitle = commerceHistoryModal?.type === "payments" ? "Payments" : "Subscriptions";

    return (
        <>
            <GuideDashboardHeader />
            <section className="main-body-section finances-section">
                <div className="manage-finances-container">
                    <h2>Manage Finances</h2>
                    {customerToast && (
                        <div
                            style={{
                                position: 'sticky',
                                top: 16,
                                zIndex: 10,
                                marginBottom: 16,
                                padding: '12px 16px',
                                borderRadius: 10,
                                color: customerToast.type === 'success' ? '#166534' : '#991b1b',
                                background: customerToast.type === 'success' ? '#dcfce7' : '#fee2e2',
                                border: `1px solid ${customerToast.type === 'success' ? '#86efac' : '#fecaca'}`
                            }}
                        >
                            {customerToast.message}
                        </div>
                    )}
                    {commerceHistoryModal && commerceHistoryCustomer && (
                        <div
                            role="presentation"
                            onClick={closeCommerceHistoryModal}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 1000,
                                background: 'rgba(15, 23, 42, 0.48)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 16
                            }}
                        >
                            <div
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="commerce-history-title"
                                onClick={(event) => event.stopPropagation()}
                                style={{
                                    width: 'min(680px, 100%)',
                                    maxHeight: '82vh',
                                    background: '#fff',
                                    borderRadius: 12,
                                    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <div style={{ padding: '18px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                                    <div>
                                        <h3 id="commerce-history-title" style={{ margin: 0 }}>{commerceHistoryTitle}</h3>
                                        <div style={{ color: '#4b5563', fontSize: 14, marginTop: 4 }}>{commerceHistoryCustomer.customerName}</div>
                                    </div>
                                    <button onClick={closeCommerceHistoryModal}>Close</button>
                                </div>
                                <div style={{ padding: '8px 20px 20px', overflowY: 'auto', maxHeight: '62vh' }}>
                                    {commerceHistoryItems.length ? commerceHistoryItems.map((item) => (
                                        <div key={item.itemId} style={{ padding: '14px 0', borderBottom: '1px solid #e5e7eb' }}>
                                            <div style={{ fontWeight: 600 }}>{item.displayName}</div>
                                            <div style={{ color: '#4b5563', fontSize: 14 }}>{formatMoneyFromCents(item.amount, item.currency)} · {item.status}</div>
                                            {commerceHistoryModal.type === "payments" ? (
                                                <div style={{ color: '#6b7280', fontSize: 12 }}>Purchased {formatDateTime(item.purchasedAt)}</div>
                                            ) : (
                                                <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                    Started {formatDateTime(item.startedAt)}
                                                    {item.renewsAt ? ` · Renews ${formatDateTime(item.renewsAt)}` : ''}
                                                </div>
                                            )}
                                        </div>
                                    )) : (
                                        <div style={{ color: '#6b7280', fontSize: 14, padding: '16px 0' }}>No {commerceHistoryTitle.toLowerCase()} yet.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="tab-segment tab-group">
                        <div id="your-products-tab" className={`tab-button ${showSection === "your-products" ? "selected" : ""}`} onClick={() => setShowSection("your-products")}>Your Products</div>
                        <div id="customers-tab" className={`tab-button ${showSection === "customers" ? "selected" : ""}`} onClick={() => setShowSection("customers")}>Customers</div>
                        <div id="financial-dashboard-tab" className={`tab-button ${showSection === "financial-dashboard" ? "selected" : ""}`} onClick={() => setShowSection("financial-dashboard")}>Financial Dashboard</div>
                    </div>
                    {showSection === "your-products" && (
                        <div id="your-products-section" className="finances-subsection">
                            <p>Create and offer conversational products through your Boggler profile. For each experience you offer, you can set a price. Remember, you can always adjust these later based on demand and feedback!</p>

                            {/* Add Product UI */}
                            <div style={{ marginBottom: 32, padding: 20, border: '1px solid #e0e0e0', borderRadius: 10, background: '#fafbfc' }}>
                                <h3 style={{ marginBottom: 12 }}>Add a Product</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 12 }}>
                                    <select value={productCategory} onChange={e => { setProductCategory(e.target.value); setProductFrequency(""); setProductSubcategory(""); }} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 160 }}>
                                        <option value="">Select Category</option>
                                        {productCategories.map(category => (
                                            <option key={category} value={category}>{productCategoryLabels[category]}</option>
                                        ))}
                                    </select>
                                    {productCategory === "subscription" && (
                                        <select value={productFrequency} onChange={e => setProductFrequency(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 120 }}>
                                            <option value="">Select Frequency</option>
                                            {subscriptionFrequencies.map(freq => <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>)}
                                        </select>
                                    )}
                                    {productCategory === "text-subscription" && (
                                        <select value={productFrequency} onChange={e => setProductFrequency(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 120 }}>
                                            <option value="">Select Frequency</option>
                                            {textSubscriptionFrequencies.map(freq => <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>)}
                                        </select>
                                    )}
                                    {productCategory === "text-and-call-support" && (
                                        <select value={productFrequency} onChange={e => setProductFrequency(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 120 }}>
                                            <option value="">Select Frequency</option>
                                            {textAndCallSupportFrequencies.map(freq => <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>)}
                                        </select>
                                    )}
                                    {(productCategory === "onetime" || productCategory === "subscription") && (
                                    <select value={productSubcategory} onChange={e => setProductSubcategory(e.target.value)} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}>
                                        <option value="">Select Subcategory</option>
                                        {productSubcategories.map((sub, index) => <option key={sub} value={sub}>{productSubcategoryNames[index]}</option>)}
                                    </select>
                                    )}
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        placeholder="Set price (USD)"
                                        value={productPrice}
                                        onChange={e => setProductPrice(e.target.value)}
                                        style={{ padding: 8, width: 120, fontSize: '1rem', borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                    <button
                                        className="cta-button"
                                        disabled={productLoading || !productCategory || (!isSupportOnlyProductCategory(productCategory) && !productSubcategory) || (productCategory !== "onetime" && !productFrequency) || !productPrice}
                                        onClick={handleUpsertProduct}
                                        style={{ minWidth: 120 }}
                                    >{productLoading ? "Adding..." : "Add Product"}</button>
                                </div>
                                {productSuccess && <div style={{ color: 'green', marginTop: 8 }}>{productSuccess}</div>}
                                {productError && <div style={{ color: 'red', marginTop: 8 }}>{productError}</div>}
                            </div>

                            {/* Existing Products UI */}
                            <div style={{ marginBottom: 32 }}>
                                <h3>Existing Products</h3>
                                <p style={{fontSize: "12px"}}>The Boggler charges 20% of set price from calls connected with a maximum boggler fee of $10 per call. If the set price is below $9.99 the boggler charges 10%.</p>
                                {/* One-Time Follow-up Call Products */}
                                {guideProfileData?.productPrices?.onetime?.intro && (
                                    <div style={{ marginBottom: 18 }}>
                                        <h4 style={{ marginBottom: 6 }}>One-Time Follow-up Call Products</h4>
                                        {Object.entries(guideProfileData.productPrices.onetime.intro).map(([subcategory, prod]) => {
                                            const isEditing = editingProduct && editingProduct.type === "onetime" && editingProduct.subcategory === subcategory;
                                            return (
                                                <div key={subcategory} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                                                    <span style={{ minWidth: 140 }}>{subcategory}</span>
                                                    {isEditing ? (
                                                        <>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="1"
                                                                value={editingProductPrice}
                                                                onChange={e => setEditingProductPrice(e.target.value)}
                                                                style={{ padding: 6, width: 90, fontSize: '1rem', borderRadius: 6, border: '1px solid #ccc' }}
                                                            />
                                                            <br />
                                                            <button className="cta-button" style={{ minWidth: 60 }} disabled={editingProductLoading || !editingProductPrice} onClick={handleUpsertProduct}>{editingProductLoading ? "Saving..." : "Save"}</button>
                                                            <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct(null); setEditingProductPrice(""); }}>Cancel</button>
                                                            {editingProductError && <span style={{ color: 'red', marginLeft: 8 }}>{editingProductError}</span>}
                                                        </>
                                                    ) : (
                                                        <div>
                                                            <div>
                                                                <span style={{ color: '#24afc2', fontWeight: 500 }}>${(prod.amount / 100).toFixed(2)}</span>
                                                                <span>{" "}</span>
                                                                <span style={{ color: '#888', fontSize: 13 }}>{prod.currency?.toUpperCase() || 'USD'}</span>
                                                                <span style={{ marginLeft: 12, color: '#2e7d32', fontSize: 13 }}>
                                                                    {(() => {
                                                                        const price = prod.amount / 100;
                                                                        const fee = price < 9.99 ? price * 0.1 : Math.min(price * 0.2, 10);
                                                                        return `Your Earnings: $${(price - fee).toFixed(2)}`;
                                                                    })()}
                                                                </span>
                                                            </div>
                                                            <div style={{marginTop: 4}}>
                                                                <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct({ type: "onetime", subcategory }); setEditingProductPrice((prod.amount / 100).toString()); }}>Edit</button>
                                                                <button style={{ minWidth: 60, color: '#c00' }} disabled={deletingProductLoading && deletingProduct && deletingProduct.type === "onetime" && deletingProduct.subcategory === subcategory} onClick={() => handleDeleteProduct({ type: "onetime", subcategory })}>
                                                                    {deletingProductLoading && deletingProduct && deletingProduct.type === "onetime" && deletingProduct.subcategory === subcategory ? "Deleting..." : "Delete"}
                                                                </button>
                                                                {deletingProductError && deletingProduct && deletingProduct.type === "onetime" && deletingProduct.subcategory === subcategory && (
                                                                    <span style={{ color: 'red', marginLeft: 8 }}>{deletingProductError}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* Call Subscription Products */}
                                {guideProfileData?.productPrices?.subscription && (
                                    <div style={{ marginBottom: 18 }}>
                                        <h4 style={{ marginBottom: 6 }}>Call Subscription Products</h4>
                                        {Object.entries(guideProfileData.productPrices.subscription).map(([frequency, freqObj]) => (
                                            <div key={frequency} style={{ marginBottom: 8 }}>
                                                <div style={{ fontWeight: 500, color: '#fe9628', marginBottom: 2 }}>{frequency.charAt(0).toUpperCase() + frequency.slice(1)}</div>
                                                {Object.entries(freqObj).map(([subcategory, prod]) => {
                                                    const isEditing = editingProduct && editingProduct.type === "subscription" && editingProduct.frequency === frequency && editingProduct.subcategory === subcategory;
                                                    return (
                                                        <div key={subcategory} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                                                            <span style={{ minWidth: 140 }}>{subcategory}</span>
                                                            {isEditing ? (
                                                                <>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        value={editingProductPrice}
                                                                        onChange={e => setEditingProductPrice(e.target.value)}
                                                                        style={{ padding: 6, width: 90, fontSize: '1rem', borderRadius: 6, border: '1px solid #ccc' }}
                                                                    />
                                                                    <button className="cta-button" style={{ minWidth: 60 }} disabled={editingProductLoading || !editingProductPrice} onClick={handleUpsertProduct}>{editingProductLoading ? "Saving..." : "Save"}</button>
                                                                    <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct(null); setEditingProductPrice(""); }}>Cancel</button>
                                                                    {editingProductError && <span style={{ color: 'red', marginLeft: 8 }}>{editingProductError}</span>}
                                                                </>
                                                            ) : (
                                                                <div>
                                                                    <div>
                                                                        <span style={{ color: '#24afc2', fontWeight: 500 }}>${(prod.amount / 100).toFixed(2)}</span>
                                                                        <span>{" "}</span>
                                                                        <span style={{ color: '#888', fontSize: 13 }}>{prod.currency?.toUpperCase() || 'USD'}</span><br />
                                                                        <span style={{ marginLeft: 0, color: '#2e7d32', fontSize: 13 }}>
                                                                            {(() => {
                                                                                const price = prod.amount / 100;
                                                                                const fee = price < 9.99 ? price * 0.1 : Math.min(price * 0.2, 10);
                                                                                return `Your Earnings: $${(price - fee).toFixed(2)}`;
                                                                            })()}
                                                                        </span>
                                                                    </div>
                                                                    <div style={{ marginTop: 4 }}>
                                                                        <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct({ type: "subscription", frequency, subcategory }); setEditingProductPrice((prod.amount / 100).toString()); }}>Edit</button>
                                                                        <button style={{ minWidth: 60, color: '#c00' }} disabled={deletingProductLoading && deletingProduct && deletingProduct.type === "subscription" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory} onClick={() => handleDeleteProduct({ type: "subscription", frequency, subcategory })}>
                                                                            {deletingProductLoading && deletingProduct && deletingProduct.type === "subscription" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory ? "Deleting..." : "Delete"}
                                                                        </button>
                                                                        {deletingProductError && deletingProduct && deletingProduct.type === "subscription" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory && (
                                                                            <span style={{ color: 'red', marginLeft: 8 }}>{deletingProductError}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Text Subscription Products */}
                                {guideProfileData?.productPrices?.["text-subscription"] && (
                                    <div style={{ marginBottom: 18 }}>
                                        <h4 style={{ marginBottom: 6 }}>Text Subscription Products</h4>
                                        {Object.entries(guideProfileData.productPrices["text-subscription"]).map(([frequency, freqObj]) => (
                                            <div key={frequency} style={{ marginBottom: 8 }}>
                                                <div style={{ fontWeight: 500, color: '#fe9628', marginBottom: 2 }}>{frequency.charAt(0).toUpperCase() + frequency.slice(1)}</div>
                                                {Object.entries(freqObj).map(([subcategory, prod]) => {
                                                    const isEditing = editingProduct && editingProduct.type === "text-subscription" && editingProduct.frequency === frequency && editingProduct.subcategory === subcategory;
                                                    return (
                                                        <div key={subcategory} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                                                            {isEditing ? (
                                                                <>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        value={editingProductPrice}
                                                                        onChange={e => setEditingProductPrice(e.target.value)}
                                                                        style={{ padding: 6, width: 90, fontSize: '1rem', borderRadius: 6, border: '1px solid #ccc' }}
                                                                    />
                                                                    <button className="cta-button" style={{ minWidth: 60 }} disabled={editingProductLoading || !editingProductPrice} onClick={handleUpsertProduct}>{editingProductLoading ? "Saving..." : "Save"}</button>
                                                                    <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct(null); setEditingProductPrice(""); }}>Cancel</button>
                                                                    {editingProductError && <span style={{ color: 'red', marginLeft: 8 }}>{editingProductError}</span>}
                                                                </>
                                                            ) : (
                                                                <div>
                                                                    <div>
                                                                        <span style={{ color: '#24afc2', fontWeight: 500 }}>${(prod.amount / 100).toFixed(2)}</span>
                                                                        <span>{" "}</span>
                                                                        <span style={{ color: '#888', fontSize: 13 }}>{prod.currency?.toUpperCase() || 'USD'}</span><br />
                                                                        <span style={{ marginLeft: 0, color: '#2e7d32', fontSize: 13 }}>
                                                                            {(() => {
                                                                                const price = prod.amount / 100;
                                                                                const fee = price < 9.99 ? price * 0.1 : Math.min(price * 0.2, 10);
                                                                                return `Your Earnings: $${(price - fee).toFixed(2)}`;
                                                                            })()}
                                                                        </span>
                                                                    </div>
                                                                    <div style={{ marginTop: 4 }}>
                                                                        <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct({ type: "text-subscription", frequency, subcategory }); setEditingProductPrice((prod.amount / 100).toString()); }}>Edit</button>
                                                                        <button style={{ minWidth: 60, color: '#c00' }} disabled={deletingProductLoading && deletingProduct && deletingProduct.type === "text-subscription" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory} onClick={() => handleDeleteProduct({ type: "text-subscription", frequency, subcategory })}>
                                                                            {deletingProductLoading && deletingProduct && deletingProduct.type === "text-subscription" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory ? "Deleting..." : "Delete"}
                                                                        </button>
                                                                        {deletingProductError && deletingProduct && deletingProduct.type === "text-subscription" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory && (
                                                                            <span style={{ color: 'red', marginLeft: 8 }}>{deletingProductError}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Text and Call Support Products */}
                                {guideProfileData?.productPrices?.["text-and-call-support"] && (
                                    <div style={{ marginBottom: 18 }}>
                                        <h4 style={{ marginBottom: 6 }}>Text and Call Support Products</h4>
                                        {Object.entries(guideProfileData.productPrices["text-and-call-support"]).map(([frequency, freqObj]) => (
                                            <div key={frequency} style={{ marginBottom: 8 }}>
                                                <div style={{ fontWeight: 500, color: '#fe9628', marginBottom: 2 }}>{frequency.charAt(0).toUpperCase() + frequency.slice(1)}</div>
                                                {Object.entries(freqObj).map(([subcategory, prod]) => {
                                                    const isEditing = editingProduct && editingProduct.type === "text-and-call-support" && editingProduct.frequency === frequency && editingProduct.subcategory === subcategory;
                                                    return (
                                                        <div key={subcategory} style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                                                            {isEditing ? (
                                                                <>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        value={editingProductPrice}
                                                                        onChange={e => setEditingProductPrice(e.target.value)}
                                                                        style={{ padding: 6, width: 90, fontSize: '1rem', borderRadius: 6, border: '1px solid #ccc' }}
                                                                    />
                                                                    <button className="cta-button" style={{ minWidth: 60 }} disabled={editingProductLoading || !editingProductPrice} onClick={handleUpsertProduct}>{editingProductLoading ? "Saving..." : "Save"}</button>
                                                                    <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct(null); setEditingProductPrice(""); }}>Cancel</button>
                                                                    {editingProductError && <span style={{ color: 'red', marginLeft: 8 }}>{editingProductError}</span>}
                                                                </>
                                                            ) : (
                                                                <div>
                                                                    <div>
                                                                        <span style={{ color: '#24afc2', fontWeight: 500 }}>${(prod.amount / 100).toFixed(2)}</span>
                                                                        <span>{" "}</span>
                                                                        <span style={{ color: '#888', fontSize: 13 }}>{prod.currency?.toUpperCase() || 'USD'}</span><br />
                                                                        <span style={{ marginLeft: 0, color: '#2e7d32', fontSize: 13 }}>
                                                                            {(() => {
                                                                                const price = prod.amount / 100;
                                                                                const fee = price < 9.99 ? price * 0.1 : Math.min(price * 0.2, 10);
                                                                                return `Your Earnings: $${(price - fee).toFixed(2)}`;
                                                                            })()}
                                                                        </span>
                                                                    </div>
                                                                    <div style={{ marginTop: 4 }}>
                                                                        <button style={{ minWidth: 60 }} onClick={() => { setEditingProduct({ type: "text-and-call-support", frequency, subcategory }); setEditingProductPrice((prod.amount / 100).toString()); }}>Edit</button>
                                                                        <button style={{ minWidth: 60, color: '#c00' }} disabled={deletingProductLoading && deletingProduct && deletingProduct.type === "text-and-call-support" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory} onClick={() => handleDeleteProduct({ type: "text-and-call-support", frequency, subcategory })}>
                                                                            {deletingProductLoading && deletingProduct && deletingProduct.type === "text-and-call-support" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory ? "Deleting..." : "Delete"}
                                                                        </button>
                                                                        {deletingProductError && deletingProduct && deletingProduct.type === "text-and-call-support" && deletingProduct.frequency === frequency && deletingProduct.subcategory === subcategory && (
                                                                            <span style={{ color: 'red', marginLeft: 8 }}>{deletingProductError}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {!(guideProfileData?.productPrices?.onetime?.intro || guideProfileData?.productPrices?.subscription || guideProfileData?.productPrices?.["text-subscription"] || guideProfileData?.productPrices?.["text-and-call-support"]) && (
                                    <div style={{ color: '#888', fontStyle: 'italic', marginTop: 8 }}>No products added yet.</div>
                                )}
                            </div>

                            {/* Existing Intro Call UI (optional, can be moved below) */}
                            <div style={{ marginBottom: 28, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
                                <h3>20-Minute Intro Call</h3>
                                {introCallPrice && !editingIntroCall ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                        <div style={{ fontSize: '1.2rem', fontWeight: 500 }}>
                                            Price: <span style={{ color: '#24afc2' }}>${introCallPrice}</span><br />
                                            <span style={{ marginLeft: 0, color: '#2e7d32', fontSize: 15 }}>
                                                {(() => {
                                                    const price = +introCallPrice;
                                                    const fee = price < 9.99 ? price * 0.1 : Math.min(price * 0.2, 10);
                                                    return `Your Earnings: $${(price - fee).toFixed(2)}`;
                                                })()}
                                            </span>
                                        </div>
                                        <button className="cta-button" style={{ minWidth: 80, padding: '6px 18px' }} onClick={() => setEditingIntroCall(true)}>Edit</button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            placeholder="Set price (USD)"
                                            value={introCallPrice || ""}
                                            onChange={e => setIntroCallPrice(e.target.value)}
                                            style={{ padding: 8, width: 120, fontSize: '1rem', borderRadius: 6, border: '1px solid #ccc' }}
                                        />
                                        <button
                                            className="cta-button"
                                            disabled={introCallLoading || !introCallPrice}
                                            onClick={async () => { await handleSetIntroCallPrice(); setEditingIntroCall(false); }}
                                            style={{ minWidth: 120 }}
                                        >{introCallLoading ? "Saving..." : "Set Price"}</button>
                                        {introCallPrice && (
                                            <button style={{ marginLeft: 8, background: 'none', border: 'none', color: '#888', cursor: 'pointer' }} onClick={() => setEditingIntroCall(false)}>Cancel</button>
                                        )}
                                    </div>
                                )}
                                {introCallSuccess && <div style={{ color: 'green', marginTop: 8 }}>{introCallSuccess}</div>}
                                {introCallError && <div style={{ color: 'red', marginTop: 8 }}>{introCallError}</div>}
                            </div>
                        </div>
                    )}
                    {showSection === "customers" && (
                        <div id="customers-section" className="finances-subsection" style={{ marginTop: 20 }}>
                            <p>Review each customer’s purchases, subscriptions, and the offers you can still send from the Boggler WhatsApp number.</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
                                <div style={{ padding: 16, borderRadius: 12, background: '#faf5ff', border: '1px solid #e9d5ff' }}>
                                    <div style={{ fontSize: 13, color: '#6b21a8', marginBottom: 6 }}>Total Customers</div>
                                    <div style={{ fontSize: 28, fontWeight: 700 }}>{customerSummary.totalCustomers || 0}</div>
                                </div>
                                <div style={{ padding: 16, borderRadius: 12, background: '#ecfeff', border: '1px solid #a5f3fc' }}>
                                    <div style={{ fontSize: 13, color: '#155e75', marginBottom: 6 }}>Active Subscribers</div>
                                    <div style={{ fontSize: 28, fontWeight: 700 }}>{customerSummary.activeSubscribers || 0}</div>
                                </div>
                                <div style={{ padding: 16, borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                    <div style={{ fontSize: 13, color: '#166534', marginBottom: 6 }}>Total Customer Revenue</div>
                                    <div style={{ fontSize: 28, fontWeight: 700 }}>{formatMoneyFromCents(customerSummary.totalCustomerRevenue, customerCards[0]?.currency || 'usd')}</div>
                                </div>
                            </div>

                            {customersLoading && !customersLoaded && <p>Loading customer commerce data...</p>}
                            {customersError && <div style={{ color: '#b91c1c', marginBottom: 16 }}>{customersError}</div>}
                            {!customersLoading && customersLoaded && customerCards.length === 0 && (
                                <div style={{ color: '#6b7280', fontStyle: 'italic' }}>No customers found yet.</div>
                            )}

                            <div style={{ display: 'grid', gap: 20 }}>
                                {customerCards.map((customer) => (
                                    <div key={customer.customerId} style={{ border: '1px solid #e5e7eb', borderRadius: 16, padding: 20, background: '#fff', boxShadow: '0 10px 25px rgba(15, 23, 42, 0.04)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                {customer.profilePictureUrl ? (
                                                    <img src={customer.profilePictureUrl} alt={customer.customerName} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div style={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }}>
                                                        {getCustomerInitials(customer.customerName)}
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 style={{ margin: 0 }}>{customer.customerName}</h3>
                                                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                        <span style={{ ...getStatusStyle(customer.currentStatus), padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{(customer.currentStatus || 'unknown').replace(/-/g, ' ')}</span>
                                                        <span style={{ color: '#4b5563', fontSize: 14 }}>Spent {formatMoneyFromCents(customer.totalSpent, customer.currency)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ minWidth: 220, color: '#4b5563', fontSize: 14 }}>
                                                <div>Last interaction: {formatDateTime(customer.lastInteractionAt)}</div>
                                                {customer.lastPromptSentAt && <div>Last prompt sent: {formatDateTime(customer.lastPromptSentAt)}</div>}
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
                                            <div style={{ padding: 16, borderRadius: 12, background: '#fafafa', border: '1px solid #f1f5f9' }}>
                                                <h4 style={{ marginTop: 0, marginBottom: 12 }}>Payments</h4>
                                                {customer.payments?.length ? (
                                                    <div style={{ display: 'grid', gap: 12 }}>
                                                        <div style={{ color: '#4b5563', fontSize: 14 }}>{customer.payments.length} payment{customer.payments.length === 1 ? '' : 's'}</div>
                                                        <div style={{ padding: '10px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                                                            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Latest payment</div>
                                                            <div style={{ fontWeight: 600 }}>{customer.payments[0].displayName}</div>
                                                            <div style={{ color: '#4b5563', fontSize: 14 }}>{formatMoneyFromCents(customer.payments[0].amount, customer.payments[0].currency)} · {customer.payments[0].status}</div>
                                                            <div style={{ color: '#6b7280', fontSize: 12 }}>Purchased {formatDateTime(customer.payments[0].purchasedAt)}</div>
                                                        </div>
                                                        <button onClick={() => openCommerceHistoryModal(customer.customerId, "payments")}>View all payments</button>
                                                    </div>
                                                ) : <div style={{ color: '#6b7280', fontSize: 14 }}>No one-time payments yet.</div>}
                                            </div>

                                            <div style={{ padding: 16, borderRadius: 12, background: '#fafafa', border: '1px solid #f1f5f9' }}>
                                                <h4 style={{ marginTop: 0, marginBottom: 12 }}>Subscriptions</h4>
                                                {customer.subscriptions?.length ? (
                                                    <div style={{ display: 'grid', gap: 12 }}>
                                                        <div style={{ color: '#4b5563', fontSize: 14 }}>{customer.subscriptions.length} subscription{customer.subscriptions.length === 1 ? '' : 's'}</div>
                                                        <div style={{ padding: '10px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                                                            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Latest subscription</div>
                                                            <div style={{ fontWeight: 600 }}>{customer.subscriptions[0].displayName}</div>
                                                            <div style={{ color: '#4b5563', fontSize: 14 }}>{formatMoneyFromCents(customer.subscriptions[0].amount, customer.subscriptions[0].currency)} · {customer.subscriptions[0].status}</div>
                                                            <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                                Started {formatDateTime(customer.subscriptions[0].startedAt)}
                                                                {customer.subscriptions[0].renewsAt ? ` · Renews ${formatDateTime(customer.subscriptions[0].renewsAt)}` : ''}
                                                            </div>
                                                        </div>
                                                        <button onClick={() => openCommerceHistoryModal(customer.customerId, "subscriptions")}>View all subscriptions</button>
                                                    </div>
                                                ) : <div style={{ color: '#6b7280', fontSize: 14 }}>No subscriptions yet.</div>}
                                            </div>

                                            <div style={{ padding: 16, borderRadius: 12, background: '#fffaf0', border: '1px solid #fde68a' }}>
                                                <h4 style={{ marginTop: 0, marginBottom: 12 }}>Available Offers</h4>
                                                {customer.availableOffers?.length ? (() => {
                                                    const selectedOfferKey = getSelectedOfferKey(customer);
                                                    const selectedOffer = getSelectedOffer(customer);
                                                    const sending = selectedOffer && sendingPromptKey === makeOfferKey(customer.customerId, selectedOffer);
                                                    const customerCannotSendPrompt = customer.canSendPrompt === false;
                                                    const offerCannotPrompt = selectedOffer?.canPrompt === false;
                                                    const sendDisabled = !selectedOffer || customerCannotSendPrompt || offerCannotPrompt || sending;

                                                    return (
                                                        <div style={{ display: 'grid', gap: 12 }}>
                                                            <select
                                                                value={selectedOfferKey}
                                                                onChange={(event) => handleSelectedOfferChange(customer.customerId, event.target.value)}
                                                                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 14 }}
                                                            >
                                                                {customer.availableOffers.map((offer) => {
                                                                    const offerKey = makeOfferKey(customer.customerId, offer);

                                                                    return (
                                                                        <option key={offerKey} value={offerKey}>
                                                                            {offer.displayName} - {formatMoneyFromCents(offer.amount, offer.currency)}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                            {selectedOffer && (
                                                                <div style={{ color: '#4b5563', fontSize: 14 }}>
                                                                    {formatMoneyFromCents(selectedOffer.amount, selectedOffer.currency)}
                                                                    {selectedOffer.frequency ? ` · ${selectedOffer.frequency}` : ''}
                                                                    {selectedOffer.status ? ` · ${selectedOffer.status}` : ''}
                                                                </div>
                                                            )}
                                                            <button type="button" className="cta-button" disabled={sendDisabled} onClick={() => handlePromptSend(customer.customerId, selectedOffer)}>
                                                                {sending ? 'Sending...' : 'Send Invoice'}
                                                            </button>
                                                            {offerCannotPrompt && selectedOffer.reasonUnavailable && (
                                                                <div style={{ color: '#6b7280', fontSize: 12 }}>{selectedOffer.reasonUnavailable}</div>
                                                            )}
                                                            {!offerCannotPrompt && customerCannotSendPrompt && (
                                                                <div style={{ color: '#6b7280', fontSize: 12 }}>Invoices are temporarily unavailable for this customer.</div>
                                                            )}
                                                        </div>
                                                    );
                                                })() : <div style={{ color: '#6b7280', fontSize: 14 }}>No available offers for this customer.</div>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {showSection === "financial-dashboard" && (
                        <div id="financial-dashboard-section" style={{ marginTop: 20 }}>
                            {useAlternatePaymentProcessor && (
                                <button
                                    onClick={() => {
                                        setProcessorNotConnected(true); 
                                        setProcessorNotConfirmed(!processorNotConfirmed);
                                }}
                            >
                                Edit Payout Info
                            </button>
                        )}
                        {!useAlternatePaymentProcessor && !checkingConnect && !stripeConnected && (
                            <div>
                                <p>Your Stripe account is not connected. Please connect to receive payments.</p>
                                {!loadingConnect && <button className="cta-button" onClick={onBoardToStripe}>Connect with Stripe</button>}
                                {loadingConnect && <p>Loading...</p>}
                            </div>
                        )}
                        {!useAlternatePaymentProcessor && checkingConnect && (
                            <p>Checking Stripe connection status...</p>
                        )}
                        {!useAlternatePaymentProcessor && !checkingConnect && stripeConnected && stripeAccountUrl && (
                            <div>
                                <p>Your Stripe account is connected.</p>
                                <button className="open-stripe-dashboard-button" onClick={() => window.open(stripeAccountUrl, "_blank")}>Go to Financial Dashboard</button>
                            </div>
                        )}
                        {useAlternatePaymentProcessor && processorNotConnected && processorNotConfirmed && (
                            <div>
                                <p>It looks like {paymentProcessor?.name} is the best option for you.</p>
                                <img src={paymentProcessor?.logo} alt={`${paymentProcessor?.name} logo`} style={{ width: 100, marginBottom: 20 }} />
                                <div>
                                    <p>Please provide details for onboarding to Wise...</p>
                                    <input
                                        type="text"
                                        placeholder="Email associated with your Wise account"
                                        style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                        value={wiseEmail}
                                        onChange={e => setWiseEmail(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Full name as per your ID"
                                        style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                        value={wiseFullName}
                                        onChange={e => setWiseFullName(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        placeholder="City of residence"
                                        style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                        value={wiseCityOfResidence}
                                        onChange={e => setWiseCityOfResidence(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        placeholder="UPI ID"
                                        style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                        value={wiseUpiId}
                                        onChange={e => setWiseUpiId(e.target.value)}
                                    />
                                    <div>
                                        <div>Address:</div>
                                        <input
                                            type="text"
                                            placeholder="Street address"
                                            style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                            value={wiseStreetAddress}
                                            onChange={e => setWiseStreetAddress(e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            placeholder="City"
                                            style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                            value={wiseCity}
                                            onChange={e => setWiseCity(e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Postal code"
                                            style={{ padding: 10, width: '100%', maxWidth: 400, marginBottom: 20 }}
                                            value={wisePostalCode}
                                            onChange={e => setWisePostalCode(e.target.value)}
                                        />
                                    </div>
                                    <button className="cta-button" onClick={handleWiseSubmit}>Connect with Wise</button>
                                </div>
                            </div>
                        )}
                        {useAlternatePaymentProcessor && !processorNotConnected && processorNotConfirmed && (
                            <div>
                                <p>Your {paymentProcessor?.name} details have been submitted and are being processed. We will reach out to you once everything is set up, which usually takes 1-2 business days. Thank you for your patience!</p>
                            </div>
                        )}
                        {useAlternatePaymentProcessor && !processorNotConnected && !processorNotConfirmed && (
                            <div>
                                <p>Your {paymentProcessor?.name} account is connected and confirmed. Thank you!</p>
                            </div>
                        )}
                        {useAlternatePaymentProcessor && paymentProcessor?.name === "wise" && !processorNotConfirmed && (
                            <div>
                                <div>
                                    <h3>Your Earnings</h3>
                                    <p>Total Earnings: ${totalEarnings.toFixed(2)}</p>
                                    <p><b>Available to Cash Out:</b><b style={{ color: 'green' }}> ${availableEarnings.toFixed(2)}</b></p>
                                    <p>Already Cashed Out: ${cashedOutEarnings.toFixed(2)}</p>
                                </div>
                                <div>
                                    {lastPayoutRequestData ? (
                                        <div style={{ marginTop: 20 }}>
                                            <h4>Last Payout Request</h4>
                                            <p>Amount: ${lastPayoutRequestData.amount.toFixed(2)}</p>
                                            <p>Date: {moment(lastPayoutRequestData.date).format('YYYY-MM-DD h:mma')}</p>
                                            <p>Status: {lastPayoutRequestData.status}</p>
                                        </div>
                                    ) : (
                                        <p style={{ marginTop: 20 }}>You have no pending payout requests.</p>
                                    )}
                                    {hasFundsToCashOut && <button className="request-payout cta" onClick={handleRequestPayout}>Request Payout</button>}
                                </div>
                            </div>
                        )}
                    </div>)}
                </div>
            </section>
            <Footer />
        </>
    );
}

export default ManageFinances;
